import React, { useCallback, useState } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";

import Loading from "../../../common/Loading";
import { asCurrency } from "../../../../utils";
import { Box } from "@material-ui/core";
import CustomCardHeader from "../CustomCardHeader";
import CashDialog from "./CashDialog";
import Button from "components/CustomButtons/Button";
import { Close, Edit } from "@material-ui/icons";

import { REPORT_ITEM_TYPE, REPORT_ITEM_SUB_TYPE } from "../../../../constants";

const useStyles = makeStyles({
  address: {
    whiteSpace: "pre-line",
    fontSize: "1rem",
    fontWeight: 400
  },
  actions: {
    margin: "0 0 0 5px",
    padding: 5,
    "& svg": {
      margin: 0
    }
  }
});

export const CashContent = ({
  reportID,
  chapterFinancial,
  totalCash,
  createFinancialAccount,
  updateFinancialAccount,
  removeFinancialAccount,
  onRefetch,
  onHelp,
  isEditable
}) => {
  const classes = useStyles();

  const [activeData, setActiveData] = useState({
    items: {},
    isCreating: true
  });
  const [dialogOpen, setDialogOpen] = useState(false);

  const [isDirty, setDirty] = useState(false);

  const items =
    chapterFinancial &&
    chapterFinancial.report &&
    chapterFinancial.report.financialAccounts.reduce((prev, cur) => {
      if (prev[cur.name]) {
        return {
          ...prev,
          [cur.name]: [...prev[cur.name], cur]
        };
      } else {
        return {
          ...prev,
          [cur.name]: [cur]
        };
      }
    }, {});

  const onCreate = useCallback(
    async item => {
      const { name, address, aba, signers, accountTypes } = item;

      if (reportID !== "add") {
        await Promise.all(
          accountTypes.map(({ accountno, balance, financialAccountTypeID }) =>
            createFinancialAccount({
              name,
              address,
              aba,
              signers,
              accountno,
              balance,
              financialAccountTypeID,
              reportID
            })
          )
        );
      }

      onRefetch();
      setDialogOpen(false);
    },
    [reportID]
  );

  const onUpdate = useCallback(
    async ({ addedItems, updatedItems, removedItems }) => {
      if (reportID !== "add") {
        await Promise.all(
          addedItems.map(item => createFinancialAccount({ ...item, reportID }))
        );

        await Promise.all(
          updatedItems.map(item =>
            updateFinancialAccount({ ...item, reportID })
          )
        );

        await Promise.all(
          removedItems.map(item => removeFinancialAccount(item.id))
        );
      }

      onRefetch();
      setDialogOpen(false);
    },
    [reportID, activeData]
  );

  const onRemove = useCallback(async subItems => {
    setDirty(true);
    await Promise.all(subItems.map(item => removeFinancialAccount(item.id)));
    onRefetch();
  }, []);

  const openCreateDialog = useCallback(() => {
    setActiveData({
      items: {},
      isCreating: true
    });
    setDialogOpen(true);
  }, []);

  const openUpdateDialog = useCallback(items => {
    setActiveData({
      items,
      isCreating: false
    });
    setDialogOpen(true);
  }, []);

  return (
    <React.Fragment>
      <CustomCardHeader
        label={"Cash & Investments"}
        onHelp={() =>
          onHelp(
            REPORT_ITEM_TYPE.EXPENSE,
            REPORT_ITEM_SUB_TYPE.OPERATING_INCOME
          )
        }
      />
      <Box mb={1} />
      <Box>
        {isDirty ? (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Loading />
          </div>
        ) : (
          <>
            {" "}
            {items &&
              Object.keys(items).map(type => {
                const subItems = items[type];
                return (
                  <Box key={`expense-${type}`} mb={2}>
                    <CustomCardHeader
                      label={subItems[0].name}
                      extraContent={
                        isEditable && (
                          <Box display="flex">
                            <Button
                              simple
                              color="success"
                              className={classes.actions}
                              onClick={() => openUpdateDialog(subItems, false)}
                            >
                              <Edit />
                            </Button>
                            <Button
                              simple
                              color="danger"
                              onClick={() => onRemove(subItems)}
                              className={classes.actions}
                            >
                              <Close />
                            </Button>
                          </Box>
                        )
                      }
                    />
                    <Box mb={3} className={classes.address}>
                      {subItems[0].address}
                    </Box>
                    <Typography>
                      ABA routing number: {subItems[0].aba}
                    </Typography>
                    <Typography>
                      Authorized signers: {subItems[0].signers}
                    </Typography>
                    {subItems.map(item => (
                      <Grid
                        container
                        justify="space-between"
                        key={`cash-${item.id}`}
                      >
                        <Typography>
                          {item.financialAccountType &&
                            item.financialAccountType.name}
                          : {item.accountno}
                        </Typography>
                        <Typography>{asCurrency(item.balance || 0)}</Typography>
                      </Grid>
                    ))}
                  </Box>
                );
              })}
            {isEditable && (
              <Box ml={3} mb={2}>
                <a onClick={openCreateDialog}>
                  <strong>Add account information</strong>
                </a>
              </Box>
            )}
          </>
        )}
        <CustomCardHeader
          label={"Total cash and investments"}
          extraContent={<Typography>{asCurrency(totalCash)}</Typography>}
        />
      </Box>
      <CashDialog
        open={dialogOpen}
        data={activeData}
        onClose={() => setDialogOpen(false)}
        onCreate={onCreate}
        onUpdate={onUpdate}
      />
    </React.Fragment>
  );
};

export default CashContent;
